import { z } from 'zod';
import { baseMessageSchema } from './_base';

export enum LobbyMessageType {
  LOBBY_LEAVE = 'LOBBY_LEAVE',
  LOBBY_LEAVE_SUCCESS = 'LOBBY_LEAVE_SUCCESS',
  LOBBY_ACCEPT = 'LOBBY_ACCEPT',
  LOBBY_REMOVE_PENDING = 'LOBBY_REMOVE_PENDING',
  LOBBY_ACCEPT_SUCCESS = 'LOBBY_ACCEPT_SUCCESS',
  LOBBY_ACCEPT_ERROR = 'LOBBY_ACCEPT_ERROR',
  LOBBY_REORDER = 'LOBBY_REORDER',
  LOBBY_REORDER_SUCCESS = 'LOBBY_REORDER_SUCCESS',
  LOBBY_KICK = 'LOBBY_KICK',
  LOBBY_KICK_SUCCESS = 'LOBBY_KICK_SUCCESS',
  LOBBY_STATE_CHANGED = 'LOBBY_STATE_CHANGED',
}

export type LobbyPlayer = z.infer<typeof lobbyPlayer>;
export type LobbyState = {
  machineId: string;
  players: LobbyPlayer[];
};

export enum PlayerLobbyStatus {
  Pending = 'pending',
  Accepted = 'accepted',
}

// Common schemas
const lobbyPlayer = z.object({
  playerId: z.string(),
  status: z.nativeEnum(PlayerLobbyStatus),
  websocketId: z.string(),
  displayName: z.string(),
  position: z.number(),
  joined: z.number(),
  playCount: z.number(),
  persistentPlayerId: z.string().optional(),
});

export const lobbyOptionsSchema = z.object({
  canJoin: z.boolean(),
  maxLobbySize: z.number().or(z.literal(-1)),
  ignoreHostMaxPlayerCount: z.boolean(),
  autoAccept: z.boolean(),
});

// Payload schemas
const lobbyLeavePayloadSchema = z.object({
  invitationId: z.string(),
});

const lobbyLeaveSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const lobbyAcceptPayloadSchema = z.object({
  invitationId: z.string(),
  playerId: z.string(),
});

const lobbyRemovePendingPayloadSchema = z.object({
  invitationId: z.string(),
  playerId: z.string(),
});

const lobbyAcceptSuccessPayloadSchema = z.object({
  hostPeerId: z.string(),
  playerId: z.string(),
});

const lobbyAcceptErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const lobbyReorderPayloadSchema = z.object({
  invitationId: z.string(),
  playerOrder: z.array(z.string()), // Array of player IDs in new order
});

const lobbyReorderSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const lobbyKickPayloadSchema = z.object({
  invitationId: z.string(),
  playerId: z.string(),
  reason: z.string().optional(),
});

const lobbyKickSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const lobbyStateChangedPayloadSchema = z.object({
  invitationId: z.string(),
  players: z.array(lobbyPlayer).optional(),
});

// Full message schemas
const lobbyLeaveSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_LEAVE),
  })
  .merge(lobbyLeavePayloadSchema);

const lobbyLeaveSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_LEAVE_SUCCESS),
  })
  .merge(lobbyLeaveSuccessPayloadSchema);

const lobbyAcceptSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_ACCEPT),
  })
  .merge(lobbyAcceptPayloadSchema);

const lobbyRemovePendingSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_REMOVE_PENDING),
  })
  .merge(lobbyRemovePendingPayloadSchema);

const lobbyAcceptSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_ACCEPT_SUCCESS),
  })
  .merge(lobbyAcceptSuccessPayloadSchema);

const lobbyAcceptErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_ACCEPT_ERROR),
  })
  .merge(lobbyAcceptErrorPayloadSchema);

const lobbyReorderSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_REORDER),
  })
  .merge(lobbyReorderPayloadSchema);

const lobbyReorderSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_REORDER_SUCCESS),
  })
  .merge(lobbyReorderSuccessPayloadSchema);

const lobbyKickSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_KICK),
  })
  .merge(lobbyKickPayloadSchema);

const lobbyKickSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_KICK_SUCCESS),
  })
  .merge(lobbyKickSuccessPayloadSchema);

const lobbyStateChangedSchema = baseMessageSchema
  .extend({
    type: z.literal(LobbyMessageType.LOBBY_STATE_CHANGED),
  })
  .merge(lobbyStateChangedPayloadSchema);

export const lobbyMessageSchema = z.discriminatedUnion('type', [
  lobbyLeaveSchema,
  lobbyLeaveSuccessSchema,
  lobbyAcceptSchema,
  lobbyRemovePendingSchema,
  lobbyAcceptSuccessSchema,
  lobbyAcceptErrorSchema,
  lobbyReorderSchema,
  lobbyReorderSuccessSchema,
  lobbyKickSchema,
  lobbyKickSuccessSchema,
  lobbyStateChangedSchema,
]);

// Export all payload types
export type LobbyLeavePayload = z.infer<typeof lobbyLeavePayloadSchema>;
export type LobbyLeaveSuccessPayload = z.infer<typeof lobbyLeaveSuccessPayloadSchema>;
export type LobbyAcceptPayload = z.infer<typeof lobbyAcceptPayloadSchema>;
export type LobbyRemovePendingPayload = z.infer<typeof lobbyRemovePendingPayloadSchema>;
export type LobbyAcceptSuccessPayload = z.infer<typeof lobbyAcceptSuccessPayloadSchema>;
export type LobbyAcceptErrorPayload = z.infer<typeof lobbyAcceptErrorPayloadSchema>;
export type LobbyReorderPayload = z.infer<typeof lobbyReorderPayloadSchema>;
export type LobbyReorderSuccessPayload = z.infer<typeof lobbyReorderSuccessPayloadSchema>;
export type LobbyKickPayload = z.infer<typeof lobbyKickPayloadSchema>;
export type LobbyKickSuccessPayload = z.infer<typeof lobbyKickSuccessPayloadSchema>;
export type LobbyStateChangedPayload = z.infer<typeof lobbyStateChangedPayloadSchema>;

// Used in invitation manifest when invitation type is "lobby"
export type LobbyOptions = z.infer<typeof lobbyOptionsSchema>;

// Full message type
export type LobbyMessage = z.infer<typeof lobbyMessageSchema>;

type PayloadType<T extends LobbyMessageType> = T extends LobbyMessageType.LOBBY_LEAVE
  ? LobbyLeavePayload
  : T extends LobbyMessageType.LOBBY_LEAVE_SUCCESS
    ? LobbyLeaveSuccessPayload
    : T extends LobbyMessageType.LOBBY_ACCEPT
      ? LobbyAcceptPayload
      : T extends LobbyMessageType.LOBBY_REMOVE_PENDING
        ? LobbyRemovePendingPayload
        : T extends LobbyMessageType.LOBBY_ACCEPT_SUCCESS
          ? LobbyAcceptSuccessPayload
          : T extends LobbyMessageType.LOBBY_ACCEPT_ERROR
            ? LobbyAcceptErrorPayload
            : T extends LobbyMessageType.LOBBY_REORDER
              ? LobbyReorderPayload
              : T extends LobbyMessageType.LOBBY_REORDER_SUCCESS
                ? LobbyReorderSuccessPayload
                : T extends LobbyMessageType.LOBBY_KICK
                  ? LobbyKickPayload
                  : T extends LobbyMessageType.LOBBY_KICK_SUCCESS
                    ? LobbyKickSuccessPayload
                    : T extends LobbyMessageType.LOBBY_STATE_CHANGED
                      ? LobbyStateChangedPayload
                      : never;

export function generateLobbyMessage<T extends LobbyMessageType>(type: T, payload: PayloadType<T>): LobbyMessage {
  const base = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case LobbyMessageType.LOBBY_LEAVE:
      return lobbyLeaveSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_LEAVE_SUCCESS:
      return lobbyLeaveSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_ACCEPT:
      return lobbyAcceptSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_REMOVE_PENDING:
      return lobbyRemovePendingSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_ACCEPT_SUCCESS:
      return lobbyAcceptSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_ACCEPT_ERROR:
      return lobbyAcceptErrorSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_REORDER:
      return lobbyReorderSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_REORDER_SUCCESS:
      return lobbyReorderSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_KICK:
      return lobbyKickSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_KICK_SUCCESS:
      return lobbyKickSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case LobbyMessageType.LOBBY_STATE_CHANGED:
      return lobbyStateChangedSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}

